import { makeStyles, Spinner } from "@fluentui/react-components";
import {
  EyeOffFilled,
  WeatherPartlyCloudyDayFilled,
} from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";
import { LayoutContext } from "../../../../context/LayoutContext";
import { $cloud100, $white } from "../../../../cssjs/colors";
import { useApiCall } from "../../../../hooks/useApiCall";
import { getAirportCode } from "../../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../../store/StoreHooks";
import {
  getAirportConfig,
  roundValue,
} from "../../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../../utils/common/Constants";
import { WindIcon } from "../../../common/icons/wind/WindIcon";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import WeatherCardSvg from "./../../../../assests/weathercard.svg";
import DisplayAirport from "./DisplayAirport";

export const WeatherCard: React.FunctionComponent = (): React.JSX.Element => {
  const airport = useAppSelector((state: any) => getAirportCode(state));
  const [weatherSummary, setWeatherSummary] = useState<any>({});
  const [airportName, setAirportName] = useState<any>({});
  const temperature = roundValue(weatherSummary?.temperature?.body ?? "");
  const tempUnit = weatherSummary?.temperature?.unit ?? "";
  const wind = roundValue(weatherSummary?.wind?.body ?? "");
  const clouds = weatherSummary?.clouds?.body ?? "";
  const visibility = roundValue(weatherSummary?.visibility?.body ?? "");
  let error: any = null;
  const airportConfigurations = getAirportConfig(airport);
  const getWeatherSummaryUrl = `${process.env.REACT_APP_API_WEATHER_SUMMARY?.replace(
    AIRPORT_CODE_PARAMETER,
    airport
  )}`;
  const { globalFilterDate } = React.useContext(LayoutContext);

  const {
    data: weatherSummaryDataResponse,
    error: errorResponse,
    isLoading,
  } = useApiCall<any>({
    url: getWeatherSummaryUrl,
    dependencies: [airport, globalFilterDate],
  });

  useEffect(() => {
    setAirportName(airportConfigurations?.iataCode);
    if (weatherSummaryDataResponse) {
      setWeatherSummary(weatherSummaryDataResponse);
    }
  }, [weatherSummaryDataResponse, airport]);

  const classes = useStyles();
  return (
    <>
      {!isLoading &&
        (error || errorResponse || !weatherSummaryDataResponse) && (
          <NoDataCard message={EMPTY_STATE_MESSAGE.Weather} />
        )}
      {isLoading ? (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={`${classes.cardLoader}`}
        />
      ) : null}
      {!error && !errorResponse && !isLoading && weatherSummaryDataResponse ? (
        <div
          className={`${classes.weatherCardContainer} ${classes.defaultWeatherCardContainer}`}
        >
          <div
            className={`${classes.cardContainer} ${classes.defaultCardContainer} card1`}
            data-testid="weather-card-container"
          >
            <div className={`${classes.cardTitle} fs-40`}>
              <span>{temperature}</span>
              <sup className={`fs-24 ${classes.cardTempUnit}`}>{tempUnit}</sup>
            </div>
            <div className={`${classes.cardSubTitle}`}>
              <div className={`fs-18 ${classes.cardsubTitleIcon}`}>
                <WeatherPartlyCloudyDayFilled />
              </div>
              <div className="fs-14">{clouds}</div>
            </div>
            <DisplayAirport airport={airport} airportName={airportName} />
            <div className={`${classes.windSection}`}>
              <span className={`${classes.weatherIcon}`}>
                <WindIcon />
              </span>
              <span>{wind}</span>
              <span> {weatherSummary?.wind?.unit}</span>
            </div>
            <div className={`${classes.visibility}`}>
              <span className={`${classes.visibilityIcon}`}>
                <EyeOffFilled />
              </span>
              <span>{visibility}</span>
              <span> {weatherSummary?.visibility?.unit}</span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

const useStyles = makeStyles({
  cardLoader: {
    height: "100%",
  },
  carouselCardLoader: {
    height: "100%",
    background: $white,
    maxHeight: "203px",
  },

  cardsubTitleIcon: {
    lineHeigt: "1",
  },
  weatherCardContainer: {
    background:
      "linear-gradient(0deg, rgba(58,139,255,1) 0%, rgba(197,213,249,1) 20%, rgba(255,255,255,1) 60%)",
    height: "100%",
    maxHeight: "203px",
  },
  carouselWeatherCardContainer: {
    borderRadius: "none",
    maxHeight: "192px",
    margin: "0 16px",
  },
  defaultWeatherCardContainer: {
    borderRadius: "8px",
  },
  cardContainer: {
    display: "grid",
    gridGap: "4px",
    gridTemplateColumns: "auto 1fr auto",
    boxSizing: "border-box",
    minHeight: "100%",
    color: $cloud100,
    background: `transparent url(${WeatherCardSvg}) no-repeat`,
    backgroundSize: "contain",
    backgroundPosition: "bottom",
  },
  carouselCardContainer: {
    "&.card1": {
      gridTemplateRows: "40px 0px 16px 16px",
    },
    padding: "16px 36px 32px",
  },
  defaultCardContainer: {
    "&.card1": {
      gridTemplateRows: "36px 0px 16px 16px",
    },
    color: $cloud100,
    padding: "8px",
    borderRadius: "8px",
  },
  cardTitle: {
    gridRow: "span 2",
    background: $white,
    display: "flex",
    lineHeight: "1",
    gridAutoFow: "column",
  },
  cardSubTitle: {
    color: $cloud100,
    alignSelf: "start",
    display: "grid",
    gridTemplateColumns: "1fr",
    lineHeight: "1.2",
  },
  windSection: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gridGap: "4px",
  },
  weatherIcon: {
    paddingTop: "4px",
  },
  visibilityIcon: {
    paddingTop: "2px",
  },
  visibility: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gridGap: "4px",
    paddingTop: "2px",
  },
  timezoneText: {
    display: "grid",
    gridAutoColumns: "auto",
    gridAutoFlow: "column",
  },
  clockIcon: {
    display: "flex",
    alignItems: "center",
    paddingLeft: "4px",
  },
  cardTempUnit: {
    lineHeight: "1.3",
  },
});
